import { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import "./ListaDividaExecutada.scss";

import { localeText } from "../../../../shared/util/datatableTranslations";
import { DataGrid } from "@mui/x-data-grid";

import { generateExcelFile } from "../../../../shared/components/excelUtils/exportExcel";

const PAGESIZE = 15;

/**
 * A lista de quem deve pelo trabalho já executado.
 *
 * Sem filtro de datas, ao contrário da lista de pagamentos em atraso: aqui não
 * há data nenhuma por que filtrar — a dívida não tem prazo, tem trabalho feito
 * atrás dela.
 */
function ListaDividaExecutada({ open, data, handleCloseModal }) {
  const [shownData, setShownData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  /* O DataGrid precisa de um `id` por linha e de valores planos para ordenar.
     O servidor devolve a dívida repartida em dois objetos. */
  const linhas = (data || []).map((c) => ({
    id: c.cliente.id,
    idCliente: c.cliente.id,
    cliente: c.cliente.nome,
    contacto: c.cliente.contacto || "-",
    dividaOrcamentos: Math.round(c.orcamentos.divida),
    dividaIsolados: Math.round(c.isolados.divida),
    dividaTotal: Math.round(c.dividaTotal),
  }));

  useEffect(() => {
    if (!searchTerm) {
      setShownData(linhas);
      return;
    }

    setShownData(
      linhas.filter((l) =>
        l.cliente.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, data]);

  const moeda = (valor) => `${valor.toLocaleString("pt-Br")} CVE`;

  const columns = [
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 5,
      renderCell: (params) => (
        <span>
          <Link
            to={`/clientes/${params.row.idCliente}-debitos`}
            className="listaDivida__clientAnchor"
          >
            {params.row.cliente}
          </Link>
        </span>
      ),
    },
    {
      field: "dividaTotal",
      headerName: "Total por pagar",
      flex: 2,
      renderCell: (params) => (
        <span className="listaDividaExecutada__total">
          {moeda(params.row.dividaTotal)}
        </span>
      ),
    },
    {
      field: "dividaOrcamentos",
      headerName: "Orçamentos",
      flex: 2,
      renderCell: (params) => <span>{moeda(params.row.dividaOrcamentos)}</span>,
    },
    {
      field: "dividaIsolados",
      headerName: "Isolados",
      flex: 2,
      renderCell: (params) => <span>{moeda(params.row.dividaIsolados)}</span>,
    },
    {
      field: "contacto",
      headerName: "Contacto",
      flex: 2,
    },
  ];

  const handleExportar = () => {
    const sheetData = shownData.map((l) => ({
      Cliente: l.cliente,
      Contacto: l.contacto,
      Divida_orcamentos: l.dividaOrcamentos.toLocaleString("pt-Br"),
      Divida_tratamentos_isolados: l.dividaIsolados.toLocaleString("pt-Br"),
      Total_por_pagar: l.dividaTotal.toLocaleString("pt-Br"),
    }));

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();

    generateExcelFile(
      `Trabalho Por Pagar ${dia}-${mes}-${ano}.xlsx`,
      "Dividas",
      sheetData
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className="fluxo-atendimento__modal__box"
        id="dividaExecutada__modal"
      >
        <div className="orcamentos-por-aprovar__modal__cabecalho">
          <h1 className="fluxo-atendimento__modal__titulo">
            Trabalho executado e não pago
          </h1>

          <div className="orcamentos-por-aprovar__filtros">
            <div className="date-filters__container">
              <div className="search__datatable__container">
                <span>Procurar</span>
                <input
                  type="search"
                  className="search__datatable__input"
                  placeholder="Procurar"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                className="clear-filters-btn"
                onClick={handleExportar}
                title="Exportar Xls"
              >
                Exportar
              </button>

              <button
                className="clear-filters-btn"
                onClick={() => setSearchTerm("")}
                title="Limpar filtros"
              >
                Limpar
              </button>
            </div>
          </div>
        </div>

        <div className="orcamentos-por-aprovar__modal__container">
          <DataGrid
            autoWidth
            className="datagrid"
            rows={shownData}
            columns={columns}
            pageSize={PAGESIZE}
            rowsPerPage={PAGESIZE}
            localeText={localeText}
          />
        </div>

        <div
          style={{
            gridColumn: "1/-1",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "25px",
          }}
        >
          <span className="cancel-btn" onClick={handleCloseModal}>
            Fechar
          </span>
        </div>
      </Box>
    </Modal>
  );
}

export default memo(ListaDividaExecutada);
